import { AUTHENTICATED_ROUTE_PREFIXES } from "./orgTeamPath.shared";

/**
 * Resolves a notification `deepLink` URL into a client-side href.
 *
 * Backend deepLinks come in two shapes:
 *   1. Fully-qualified URLs that already include locale + org/team — e.g.
 *      "https://app.example.com/en/org-1/team-1/crunches/file-42?..."
 *      (the task notification source-link path).
 *   2. Bare authenticated paths — e.g. "/tasks", "/crunches/file-42?..."
 *      (older notification types that rely on the FE to scope them).
 *
 * For case 1, strip scheme/host and return the existing path. For case 2,
 * call `build()` so the link lands inside the current org/team.
 *
 * Keep in sync with the FE Source Link logic in
 * apps/web/components/Tasks/utils/sources.ts (and the BE mirror in
 * task-notifications.service.ts).
 */
export function resolveDeepLinkHref(rawUrl: string, build: (path: string) => string): string {
  const path = stripOriginIfPresent(rawUrl);

  const withoutLocale = path.replace(/^\/[a-z]{2}(?=\/|$)/, "");
  const firstSegment = withoutLocale.split("/")[1] ?? "";
  const isBareAuthenticatedRoute = (AUTHENTICATED_ROUTE_PREFIXES as readonly string[]).includes(firstSegment);

  return isBareAuthenticatedRoute ? build(path) : path;
}

function stripOriginIfPresent(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return rawUrl;
  }
}
